import { useCallback, useMemo, useRef, useEffect } from 'react';
import APIHelper from '../common/ApiHelper';
import { authHeader } from '../helper';

const APP_INSIGHT_ENDPOINT = '/coachesdb/appInsights';

/**
 * Custom hook for tracking events and exceptions to Application Insights via
 * the existing backend route (/api/content-insight/appInsights).
 *
 * Usage:
 *   const { trackEvent, trackException } = useAppInsight({
 *     config: { appId: 'coachesdb-web', debug: false },
 *     onError: (err) => console.warn(err),
 *   });
 */
export function useAppInsight(options = {}) {
  const { config = {}, onError } = options;
  const { appId, apiKey, debug } = config;

  // Keep latest onError without invalidating memoized callbacks
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  // Build request headers once per relevant config change
  const headers = useMemo(() => {
    const base = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...authHeader(),
    };
    //if (appId) base['appId'] = appId;
    //if (apiKey) base['X-API-Key'] = apiKey;
    return base;
  }, [appId, apiKey]);

  // Internal helper used by both trackEvent and trackException
  const send = useCallback(
    async (eventName, properties, type, kind) => {
      try {
        const response = await APIHelper.post(
          APP_INSIGHT_ENDPOINT,
          { eventName, properties, type },
          headers
        );
        if (debug) {
          // eslint-disable-next-line no-console
          console.log('[AppInsight] ' + kind + ' tracked:', {
            eventName,
            properties,
            type,
            response,
          });
        }
      } catch (err) {
        const message =
          err && err.message ? err.message : 'Failed to track ' + kind;
        const wrapped = err instanceof Error ? err : new Error(message);

        if (onErrorRef.current) {
          onErrorRef.current(wrapped);
        }

        // Don't re-throw - don't disrupt the user's flow
        if (debug) {
          // eslint-disable-next-line no-console
          console.error('[AppInsight] Error tracking ' + kind + ':', err);
        }
      }
    },
    [headers, debug]
  );

  // Track a custom event with optional properties
  const trackEvent = useCallback(
    (eventName, properties = {}, type) =>
      send(eventName, properties, type),
    [send]
  );

  // Track an exception
  const trackException = useCallback(
    (eventName, properties = {}, type) =>
      send(eventName, properties, type),
    [send]
  );

  // Backwards-compatible alias for the legacy useTelemetry API
  const trackCustomEvents = trackEvent;

  return {
    trackEvent,
    trackException,
    trackCustomEvents,
  };
}
